import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";

/**
 * downloadFile — util ÚNICO de download de entregáveis (F1 PR-D), usado pelo
 * chip do Mirror E pela Biblioteca (decisão do GO: nasce compartilhado).
 *
 * Estratégia de URL expirada (o "re-sign" da F1): NENHUMA edge function nova.
 * A signed URL do chat (7d app / 48h WhatsApp) pode morrer; o fallback baixa
 * direto do bucket privado com o JWT do usuário — a policy de SELECT de
 * storage do PR-A (`deliverables_storage_read_own`, pasta raiz = auth.uid())
 * já autoriza tanto `storage.download()` quanto `createSignedUrl()` para o
 * dono. Chamar o deliverable-generate para re-assinar foi descartado: o
 * branch de idempotência só cobre client_ref das últimas 24h e fora da
 * janela REGERARIA o artefato (tokens + linha duplicada).
 *
 * Web/PWA: fetch → Blob → <a download> (sem navegação). Capacitor nativo:
 * abre a URL válida no browser do sistema (precedente SettingsPage
 * window.open) — salvar via Filesystem nativo é follow-up documentado no PR.
 */

const BUCKET = "deliverables";
// TTL curto pro link efêmero minted no fluxo nativo — só precisa sobreviver
// ao handoff pro browser do sistema.
const NATIVE_FRESH_TTL_SECONDS = 300;

/** De onde saiu o arquivo — alimenta `refreshed_url` no analytics. */
export type DownloadSource = "signed_url" | "storage_refetch";

export interface DownloadDeliverableOpts {
  /** Signed URL entregue no chat (pode já ter expirado). */
  fileUrl?: string | null;
  /** Path no bucket ({user_id}/{deliverable_id}.{ext}) — fallback de re-sign. */
  storagePath?: string | null;
  /** Nome sugerido do arquivo, com extensão (ver deliverableFileName). */
  fileName: string;
}

/** "12,3 KB" / "1,4 MB" — exibição de tamanho na Biblioteca. */
export function formatFileSize(bytes: number | null | undefined, locale: string): string {
  if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes <= 0) return "";
  const units = ["B", "KB", "MB"];
  let value = bytes;
  let u = 0;
  while (value >= 1024 && u < units.length - 1) {
    value /= 1024;
    u++;
  }
  const digits = u === 0 ? 0 : 1;
  return `${value.toLocaleString(locale, { maximumFractionDigits: digits })} ${units[u]}`;
}

/** Nome de arquivo seguro a partir do título do entregável. */
export function deliverableFileName(title: string, ext: string): string {
  const base = (title || "")
    .replace(/[\\/:*?"<>|]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 60);
  return `${base || "vyv"}.${ext}`;
}

export function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke defer: alguns WebViews cancelam o download se o objectURL morre
  // no mesmo tick do click.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Baixa um entregável de ARQUIVO (docx/pdf/xlsx). Tenta a signed URL recebida;
 * se ela falhar (expirada/ausente), re-busca autenticado direto do bucket.
 * Resolve com a fonte usada; rejeita quando nenhum caminho funcionou.
 */
export async function downloadDeliverable(
  opts: DownloadDeliverableOpts,
): Promise<DownloadSource> {
  const { fileUrl, storagePath, fileName } = opts;

  if (Capacitor.isNativePlatform()) {
    // Nativo: URL garantidamente válida COM Content-Disposition: attachment
    // (PR-F fix): sem o flag `download`, o WKWebView NAVEGAVA a própria tela
    // pro viewer inline de PDF — sem voltar/fechar, usuário preso (bug de
    // produção no iPhone; TXT/XLSX não renderizam inline e escapavam). Com
    // `{ download: fileName }` a signed URL vem com attachment e o WebView
    // dispara download em vez de navegar — nos 3 formatos.
    if (storagePath) {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(storagePath, NATIVE_FRESH_TTL_SECONDS, { download: fileName });
      if (!error && data?.signedUrl) {
        window.open(data.signedUrl, "_blank", "noopener,noreferrer");
        return "storage_refetch";
      }
    }
    if (fileUrl) {
      // Fallback sem storagePath: preserva o comportamento de attachment
      // anexando o param `download` na signed URL existente (mesmo efeito
      // do flag no createSignedUrl — o storage lê da query string).
      const sep = fileUrl.indexOf("?") >= 0 ? "&" : "?";
      window.open(
        fileUrl + sep + "download=" + encodeURIComponent(fileName),
        "_blank",
        "noopener,noreferrer",
      );
      return "signed_url";
    }
    throw new Error("no download path available");
  }

  return await downloadViaBlob(opts);
}

// Fluxo blob compartilhado (era o branch web do downloadDeliverable): signed
// URL primeiro (zero round-trip extra no caso comum), fallback download
// autenticado do bucket (JWT + RLS do PR-A) — SEM navegação em nenhum caso.
async function downloadViaBlob(
  opts: DownloadDeliverableOpts,
): Promise<DownloadSource> {
  const { fileUrl, storagePath, fileName } = opts;

  if (fileUrl) {
    try {
      const res = await fetch(fileUrl);
      if (res.ok) {
        triggerBlobDownload(await res.blob(), fileName);
        return "signed_url";
      }
    } catch (e) {
      console.error("[downloadFile] signed URL fetch failed — refetching from storage:", e);
    }
  }

  if (storagePath) {
    const { data, error } = await supabase.storage.from(BUCKET).download(storagePath);
    if (error || !data) {
      console.error("[downloadFile] storage refetch failed:", error);
      throw error ?? new Error("storage download empty");
    }
    triggerBlobDownload(data, fileName);
    return "storage_refetch";
  }

  throw new Error("no download path available");
}

/**
 * Baixa um entregável de IMAGEM (PR-A F2). Diferente dos documentos, imagem
 * NUNCA passa pelo caminho nativo de window.open — PNG renderiza inline no
 * WebView/browser e prende o usuário sem volta (o bug que este PR mata).
 * Todas as plataformas usam o fluxo blob → <a download> (salva em Arquivos);
 * no mobile web, o salvar-em-FOTOS é o long-press na imagem inline da bolha
 * (padrão da plataforma — não existe API pra forçar).
 *
 * TODO(nativo): salvar-em-Fotos de verdade no Capacitor precisa de
 * @capacitor/share (+ @capacitor/filesystem pra escrever o blob e compartilhar
 * o ARQUIVO, não o link) — nenhum dos dois está instalado hoje e este PR não
 * adiciona dependência nativa (decisão de plugin pendente com o Rafa). Até lá
 * o nativo cai neste mesmo fluxo blob; se o WKWebView ignorar o <a download>
 * (limitação conhecida), o usuário ainda tem o long-press na imagem inline.
 */
export async function downloadImageDeliverable(
  opts: DownloadDeliverableOpts,
): Promise<DownloadSource> {
  return await downloadViaBlob(opts);
}

/**
 * "Baixa" um entregável de TEXTO como .txt gerado client-side — texto vive em
 * mirror_deliverables.content_text, não no storage.
 */
export function downloadTextDeliverable(title: string, content: string): void {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  triggerBlobDownload(blob, deliverableFileName(title, "txt"));
}

// ── VISUALIZAR (PR-F) — helper compartilhado do viewer da Biblioteca ────────

/**
 * Signed URL fresca SEM flag de download — fonte ÚNICA do viewer em todas as
 * superfícies (GO 17/07): modal web (<object data=...> com streaming do
 * browser), nova aba iOS Safari e @capacitor/browser no nativo (FECHAR
 * nativo). Inline aqui é o comportamento DESEJADO; o attachment fica só no
 * caminho de Baixar. Blob NÃO participa da visualização — evita segurar até
 * 20MB na heap só pra pré-visualizar.
 */
export async function createViewUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, NATIVE_FRESH_TTL_SECONDS);
  if (error || !data?.signedUrl) {
    console.error("[downloadFile] view url failed:", error);
    throw error ?? new Error("sign failed");
  }
  return data.signedUrl;
}
